// Family Groups API Client

import axios from './axios';

export interface FamilyGroupMemberRequest {
  contact_id: string;
  member_role?: string;
  can_view_invoices?: boolean;
  can_make_payments?: boolean;
  payment_percentage?: number;
  receives_separate_invoice?: boolean;
}

export interface CreateFamilyGroupRequest {
  child_id: string;
  family_name: string;
  billing_type: 'UNIFIED' | 'SPLIT';
  unified_billing_name?: string;
  unified_billing_street?: string;
  unified_billing_house_number?: string;
  unified_billing_postal_code?: string;
  unified_billing_city?: string;
  unified_billing_country?: string;
  unified_billing_email?: string;
  members: FamilyGroupMemberRequest[];
}

export interface UpdateFamilyGroupRequest {
  child_id: string;
  family_name: string;
  billing_type: 'UNIFIED' | 'SPLIT';
  unified_billing_name?: string;
  unified_billing_street?: string;
  unified_billing_house_number?: string;
  unified_billing_postal_code?: string;
  unified_billing_city?: string;
  unified_billing_country?: string;
  unified_billing_email?: string;
}

export interface FamilyGroupResponse {
  id: string;
  org_id: string;
  child_id: string;
  family_name: string;
  billing_type: string;
  unified_billing_name?: string;
  unified_billing_street?: string;
  unified_billing_house_number?: string;
  unified_billing_postal_code?: string;
  unified_billing_city?: string;
  unified_billing_country?: string;
  unified_billing_email?: string;
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
  deleted_at?: string;
}

export interface FamilyGroupMemberResponse {
  id: string;
  family_group_id: string;
  contact_id: string;
  member_role?: string;
  can_view_invoices?: boolean;
  can_make_payments?: boolean;
  payment_percentage?: number;
  receives_separate_invoice?: boolean;
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
}

export interface FamilyGroupWithMembersResponse extends FamilyGroupResponse {
  members: FamilyGroupMemberResponse[];
}

export class FamilyGroupService {
  // Create a new family group
  static async createFamilyGroup(data: CreateFamilyGroupRequest): Promise<FamilyGroupWithMembersResponse> {
    const response = await axios.post<FamilyGroupWithMembersResponse>('/api/v1/family-groups', data);
    return response.data;
  }

  // Get a family group by ID
  static async getFamilyGroup(id: string): Promise<FamilyGroupWithMembersResponse> {
    const response = await axios.get<FamilyGroupWithMembersResponse>(`/api/v1/family-groups/${id}`);
    return response.data;
  }

  // Get family groups for a child
  static async getFamilyGroupsByChild(childId: string): Promise<FamilyGroupWithMembersResponse[]> {
    const response = await axios.get<FamilyGroupWithMembersResponse[]>(`/api/v1/family-groups/child/${childId}`);
    return response.data;
  }

  // Update a family group
  static async updateFamilyGroup(id: string, data: UpdateFamilyGroupRequest): Promise<FamilyGroupResponse> {
    const response = await axios.put<FamilyGroupResponse>(`/api/v1/family-groups/${id}`, data);
    return response.data;
  }

  // Delete a family group
  static async deleteFamilyGroup(id: string): Promise<void> {
    await axios.delete(`/api/v1/family-groups/${id}`);
  }

  // Add a member to a family group
  static async addMember(groupId: string, member: FamilyGroupMemberRequest): Promise<FamilyGroupMemberResponse> {
    const response = await axios.post<FamilyGroupMemberResponse>(`/api/v1/family-groups/${groupId}/members`, member);
    return response.data;
  }

  // Update a family group member
  static async updateMember(groupId: string, memberId: string, data: Partial<FamilyGroupMemberRequest>): Promise<FamilyGroupMemberResponse> {
    const response = await axios.put<FamilyGroupMemberResponse>(`/api/v1/family-groups/${groupId}/members/${memberId}`, data);
    return response.data;
  }

  // Remove a member from a family group
  static async removeMember(groupId: string, memberId: string): Promise<void> {
    await axios.delete(`/api/v1/family-groups/${groupId}/members/${memberId}`);
  }

  // List all family groups for the organization
  static async listFamilyGroups(limit = 100, offset = 0): Promise<FamilyGroupWithMembersResponse[]> {
    const response = await axios.get<FamilyGroupWithMembersResponse[]>('/api/v1/family-groups', {
      params: { limit, offset }
    });
    return response.data;
  }
}